/**
 * MeetingReview.jsx — Meeting identification & reconciliation page
 * Route: /meeting-review/:transcriptId
 *
 * Answers: "Who attended this meeting and which TaxFlow contact does it belong to?"
 * Thin by design — no duplicate of CaseAnalysis review controls.
 *
 * Governed actions: Assign to Client · Archive / Not Client Related · Restore
 *
 * CaseAnalysis note: Review/View Analysis navigates to /case-analysis/{clientId}
 * which opens the latest analysis for that client. Direct analysis-ID routing is
 * deferred — tracked as a future enhancement.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Video, User, Archive, CheckCircle2, Clock,
  Loader2, Search, X, ExternalLink, RotateCcw, Mail,
  ChevronDown, ChevronUp, Users, FileText, Tag, UserPlus, AlertCircle, ArrowRight, RefreshCw,
  Send, Edit3,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { parseSpeakerTurns, parseTranscriptLines, fmtDuration } from "@/lib/zoomMeeting";

// ── Labels ─────────────────────────────────────────────────────────────────
const INTAKE_STATUS_LABELS = {
  high_confidence_match:    "Matched automatically — high confidence",
  exception_ambiguous:      "Multiple possible matches — review required",
  exception_missing_email:  "No usable email — manual review required",
  exception_low_confidence: "Low confidence match — review required",
  auto_lead_created:        "New lead created automatically",
  attached:                 "Attached to existing client",
  pending_review:           "Awaiting review",
};
const INTAKE_REASON_LABELS = {
  high_confidence_match: "Matched automatically",
  auto_created:          "Lead created automatically",
};
const CONFIDENCE_STYLE = {
  high:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  medium:   "bg-amber-50 text-amber-700 border-amber-200",
  possible: "bg-amber-50 text-amber-700 border-amber-200",
  low:      "bg-slate-100 text-slate-600 border-slate-200",
};

// ── CreateLeadModal ──────────────────────────────────────────────────────────
// Pre-fills lead name/email from available meeting evidence.
// After creation: assigns the meeting to the new lead + navigates to workspace.
function CreateLeadModal({ participants, prospectName, meetingTitle, transcriptId, onSuccess, onCancel }) {
  // Derive best initial values from meeting evidence
  const firstParticipant = participants.find(p => p.name) || participants[0] || {};
  const rawName = (prospectName || firstParticipant.name || "").trim();
  const nameParts = rawName.split(/\s+/);
  const defaultFirst = nameParts[0] || "";
  const defaultLast  = nameParts.slice(1).join(" ") || "";
  const defaultEmail = (participants.find(p => p.email) || {}).email || "";

  const [form, setForm] = useState({
    first_name: defaultFirst, last_name: defaultLast, email: defaultEmail,
  });
  const [busy,    setBusy]    = useState(false);
  const [dupErr,  setDupErr]  = useState(null);
  const [created, setCreated] = useState(null);  // {id, name} after successful creation
  const [inviting, setInviting] = useState(false);

  const set = (k) => (e) => { setForm(f => ({ ...f, [k]: e.target.value })); setDupErr(null); };

  const submit = async (force = false) => {
    if (!form.first_name.trim() && !form.last_name.trim()) {
      toast.error("Please enter at least a first or last name.");
      return;
    }
    setBusy(true);
    try {
      const created = await api.post("/crm/contacts", {
        contact_type: "lead",
        first_name:   form.first_name.trim(),
        last_name:    form.last_name.trim(),
        email:        form.email.trim() || null,
        lead_source:  "zoom_meeting",
        force,
      });
      // Associate meeting with the newly created lead
      await api.post(`/meeting-transcripts/${transcriptId}/assign`, {
        client_id: created.data.id,
      });
      const displayName = [form.first_name.trim(), form.last_name.trim()].filter(Boolean).join(" ") || "New lead";
      setCreated({ id: created.data.id, name: displayName, email: form.email.trim() });
    } catch (e) {
      if (e.response?.status === 409) {
        setDupErr(e.response.data?.detail || "Possible duplicate found.");
      } else {
        toast.error(e.response?.data?.detail || "Failed to create lead.");
      }
    } finally { setBusy(false); }
  };

  const sendInvite = async () => {
    if (!created) return;
    setInviting(true);
    try {
      const { data } = await api.post(`/portal/admin/${created.id}/enable`);
      if (data.email_status === "sent") {
        toast.success(`Portal invite emailed to ${data.email || created.email}.`);
      } else if (data.email_status === "suppressed_preview_guardrail") {
        toast.warning("Preview environment: email suppressed. Share the activation link from the client workspace.");
      } else if (data.email_status === "disabled") {
        toast.warning("Invitation prepared — email provider not configured. Share the activation link from the client workspace.");
      } else if (data.email_status === "error") {
        toast.warning("Invitation prepared — email delivery failed. The link remains valid. Share it from the client workspace.");
      } else {
        toast.info("Invitation prepared — email was not sent. Share the activation link from the client workspace.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send portal invite.");
    } finally {
      setInviting(false);
      onSuccess(created.id);
    }
  };

  // Success screen — shown after lead created + meeting assigned
  if (created) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="mr-create-lead-success">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
          <div className="flex items-start gap-3 mb-5">
            <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
              <CheckCircle2 className="h-4.5 w-4.5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Lead Created</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                <span className="font-semibold text-slate-700">{created.name}</span> added as a lead — meeting assigned.
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 mb-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Send Portal Invite?</p>
            <p className="text-sm text-slate-600">
              Give {created.name.split(" ")[0] || "this lead"} access to their client portal to upload documents and track progress. This sends the standard welcome email.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <button
              onClick={sendInvite}
              disabled={inviting}
              data-testid="mr-send-portal-invite-btn"
              className="inline-flex items-center justify-center gap-2 w-full text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 px-4 py-2.5 rounded-xl shadow-sm transition-colors disabled:opacity-50"
            >
              {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Send Portal Invite &amp; Go to Workspace
            </button>
            <button
              onClick={() => onSuccess(created.id)}
              data-testid="mr-skip-invite-btn"
              className="inline-flex items-center justify-center gap-2 w-full text-sm font-medium text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 px-4 py-2.5 rounded-xl transition-colors"
            >
              <ArrowRight className="h-4 w-4" /> Skip — Go to Workspace
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="mr-create-lead-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
            <UserPlus className="h-4.5 w-4.5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900">Create New Lead</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              No CRM match found. Create a new lead and assign this meeting.
            </p>
          </div>
        </div>

        <div className="space-y-3 mb-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">First name</label>
              <input value={form.first_name} onChange={set("first_name")}
                data-testid="mr-create-lead-first-name"
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                placeholder="First name" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Last name</label>
              <input value={form.last_name} onChange={set("last_name")}
                data-testid="mr-create-lead-last-name"
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                placeholder="Last name" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Email</label>
            <input value={form.email} onChange={set("email")} type="email"
              data-testid="mr-create-lead-email"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
              placeholder="email@example.com" />
          </div>
          {meetingTitle && (
            <p className="text-xs text-slate-400 flex items-center gap-1.5">
              <Video className="h-3 w-3 shrink-0" /> Source meeting: {meetingTitle}
            </p>
          )}
        </div>

        {dupErr && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2" data-testid="mr-create-lead-dup-warning">
            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Possible duplicate contact</p>
              <p className="text-xs text-amber-700 mt-0.5">A contact with this email may already exist. You can assign the meeting to the existing contact instead, or proceed to create a new lead.</p>
              <button onClick={() => submit(true)}
                data-testid="mr-create-lead-force-btn"
                className="mt-2 text-xs font-semibold text-amber-800 hover:text-amber-900 underline underline-offset-2">
                Create anyway
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onCancel} data-testid="mr-create-lead-cancel"
            className="text-sm font-medium text-slate-600 hover:text-slate-900 px-4 py-2 rounded-lg border border-slate-200">
            Cancel
          </button>
          <button onClick={() => submit(false)} disabled={busy}
            data-testid="mr-create-lead-submit"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg disabled:opacity-50 shadow-sm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Create Lead &amp; Assign Meeting
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Helpers ─────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}


// ── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ meeting }) {
  if (meeting.is_archived) return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 bg-slate-100 text-slate-600 border border-slate-200">
      <Archive className="h-3 w-3" /> Archived
    </span>
  );
  const ana = meeting.analysis;
  if (!meeting.client_id || !ana) return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-200">
      <Clock className="h-3 w-3" /> Unassigned
    </span>
  );
  if (ana.status === "ready_for_review") return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200">
      <Video className="h-3 w-3" /> Ready for Review
    </span>
  );
  if (ana.status === "approved" || ana.approval_status === "approved") return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200">
      <CheckCircle2 className="h-3 w-3" /> Approved
    </span>
  );
  if (ana.status === "processing") return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200">
      <Loader2 className="h-3 w-3 animate-spin" /> Processing
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 bg-slate-100 text-slate-600 border border-slate-200">
      <Clock className="h-3 w-3" /> {meeting.client_id ? "Assigned" : "Unassigned"}
    </span>
  );
}

// ── Meeting Preview (with lazy-loaded collapsible full transcript) ────────────
function MeetingPreviewSection({ transcriptId, transcriptCtx }) {
  const preview = transcriptCtx?.preview;
  const [expanded, setExpanded] = useState(false);
  const [fullText, setFullText] = useState(null);
  const [loadingFull, setLoadingFull] = useState(false);

  const toggleFull = async () => {
    if (fullText !== null) { setExpanded(v => !v); return; }
    setLoadingFull(true);
    try {
      const { data } = await api.get(`/meeting-transcripts/${transcriptId}/full-transcript`);
      setFullText(data.transcript_text || "");
      setExpanded(true);
    } catch { toast.error("Could not load full transcript"); }
    finally { setLoadingFull(false); }
  };

  if (!preview) return null;

  const turns = parseSpeakerTurns(preview);
  const fullLines = fullText != null ? parseTranscriptLines(fullText) : [];

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="mr-preview-section">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-3">Meeting Preview</p>

      <div className="space-y-2.5 mb-3">
        {turns.map((t, i) => (
          <div key={i} className="text-sm leading-relaxed">
            {t.speaker && <span className="font-semibold text-slate-700">{t.speaker}: </span>}
            <span className="text-slate-600 italic">"{t.text}"</span>
          </div>
        ))}
      </div>

      <button
        onClick={toggleFull}
        disabled={loadingFull}
        data-testid="mr-view-transcript-btn"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2F80ED] hover:underline disabled:opacity-50"
      >
        {loadingFull
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        {expanded ? "Hide Full Transcript" : "View Full Transcript"}
      </button>

      {expanded && (
        <>
          {fullLines.length > 0 ? (
            <div className="mt-3 max-h-72 overflow-y-auto rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1" data-testid="mr-full-transcript">
              {fullLines.map((l, i) => (
                <div key={i} className="text-xs leading-relaxed">
                  {l.speaker && <span className="font-semibold text-slate-700">{l.speaker}: </span>}
                  <span className="text-slate-600">{l.text}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-xs text-slate-400 italic" data-testid="mr-no-transcript">
              No transcript text available for this meeting.
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ── Possible Matches — strictly advisory, no auto-assign ─────────────────────
function PossibleMatchesSection({ matches, participants, transcriptCtx, onAssign }) {
  if (!matches || matches.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="mr-matches-section">
      <div className="flex items-baseline justify-between mb-3">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Possible Matches</p>
        <span className="text-[10px] text-slate-400 italic">Advisory — staff makes the final decision</span>
      </div>

      <div className="space-y-3">
        {matches.map((match) => {
          const emailMatch = participants.some(
            p => p.email && match.subtitle &&
                 p.email.toLowerCase() === match.subtitle.toLowerCase()
          );
          const confStyle = CONFIDENCE_STYLE[match.confidence] || CONFIDENCE_STYLE.low;

          return (
            <div
              key={match.id}
              className="flex items-start justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-3"
              data-testid={`mr-match-${match.id}`}
            >
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-900">{match.title}</span>
                  <span className="text-[11px] text-slate-500 bg-slate-200 rounded px-1.5 py-0.5">{match.type}</span>
                  {emailMatch && (
                    <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                      Email match
                    </span>
                  )}
                  <span className={cn("text-[11px] font-semibold border rounded px-1.5 py-0.5", confStyle)}>
                    Score {match.score} · {match.confidence}
                  </span>
                </div>
                {match.subtitle && (
                  <p className="text-xs text-slate-500 truncate">{match.subtitle}</p>
                )}
                {match.reason && (
                  <p className="text-xs text-slate-400">Evidence: {match.reason}</p>
                )}
              </div>

              <button
                onClick={() => onAssign(match)}
                data-testid={`mr-assign-candidate-${match.id}`}
                className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-[#2F80ED] hover:text-white hover:bg-[#2F80ED] border border-[#2F80ED]/40 hover:border-[#2F80ED] rounded-lg px-3 py-1.5 transition-colors"
              >
                <User className="h-3 w-3" /> Assign
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Manual assignment search panel ────────────────────────────────────────────
function AssignSearchPanel({ onSelectContact, onCancel }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounce = useRef(null);

  const doSearch = useCallback((q) => {
    clearTimeout(debounce.current);
    if (q.length < 2) { setResults([]); return; }
    debounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults((data.results || []).filter(r => r.type === "contact"));
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 280);
  }, []);

  return (
    <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50/40 p-4" data-testid="mr-assign-panel">
      <p className="text-xs font-semibold text-slate-600 mb-2">Search by name, email, or client number</p>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
        <input
          autoFocus
          value={query}
          onChange={e => { setQuery(e.target.value); doSearch(e.target.value); }}
          placeholder="Name, email, or client number…"
          className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
          data-testid="mr-assign-search-input"
        />
        {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-slate-400" />}
      </div>

      {results.length > 0 && (
        <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto" data-testid="mr-assign-results">
          {results.map(r => (
            <li key={r.id}>
              <button
                onClick={() => onSelectContact({ id: r.id, name: r.title, email: r.subtitle, type: null })}
                data-testid={`mr-assign-contact-${r.id}`}
                className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white border border-transparent hover:border-slate-200 transition-all"
              >
                <div className="h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <User className="h-3.5 w-3.5 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{r.title}</div>
                  {r.subtitle && <div className="text-xs text-slate-500 truncate">{r.subtitle}</div>}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {query.length >= 2 && !searching && results.length === 0 && (
        <p className="mt-2 text-xs text-slate-500 text-center py-2" data-testid="mr-assign-no-results">
          No clients or leads found for "{query}"
        </p>
      )}
      <div className="flex justify-end mt-3">
        <button onClick={onCancel} className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1">Cancel</button>
      </div>
    </div>
  );
}

// ── Assignment confirmation dialog ────────────────────────────────────────────
function AssignConfirmDialog({ confirm, onCancel, onConfirm, busy }) {
  if (!confirm) return null;
  const { contact, evidence } = confirm;
  const hasEvidence = evidence.score != null || evidence.reason || evidence.participant_email_match ||
                      evidence.detected_names?.length > 0 || evidence.prospect_name;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onCancel}
      data-testid="mr-assign-confirm-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm mx-4 p-6"
        onClick={e => e.stopPropagation()}
        data-testid="mr-assign-confirm-dialog"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="h-10 w-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
            <User className="h-5 w-5 text-blue-600" />
          </div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        <h3 className="text-base font-bold text-slate-900 mb-1">
          Assign this meeting to {contact.name}?
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          {[contact.type, contact.email].filter(Boolean).join(" · ")}
        </p>

        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 mb-5 text-xs space-y-1.5" data-testid="mr-assign-evidence">
          <p className="font-semibold text-[10px] uppercase tracking-wide text-slate-400 mb-2">Supporting evidence</p>

          {evidence.score != null && (
            <div className="flex gap-2">
              <span className="text-slate-500 shrink-0">Match score:</span>
              <span className="font-semibold text-slate-700">{evidence.score}</span>
            </div>
          )}
          {evidence.reason && (
            <div className="flex gap-2">
              <span className="text-slate-500 shrink-0">Evidence:</span>
              <span className="font-semibold text-slate-700">{evidence.reason}</span>
            </div>
          )}
          {evidence.detected_names?.length > 0 && (
            <div className="flex gap-2">
              <span className="text-slate-500 shrink-0">Transcript detected:</span>
              <span className="font-semibold text-slate-700">{evidence.detected_names.join(", ")}</span>
            </div>
          )}
          {evidence.prospect_name && (
            <div className="flex gap-2">
              <span className="text-slate-500 shrink-0">Prospect identified:</span>
              <span className="font-semibold text-slate-700">{evidence.prospect_name}</span>
            </div>
          )}
          {evidence.participant_email_match && (
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
              <span className="font-semibold">Participant email matches CRM contact email</span>
            </div>
          )}
          {!hasEvidence && (
            <p className="text-slate-400 italic">Selected via manual search</p>
          )}
        </div>

        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={busy}
            data-testid="mr-assign-cancel-btn"
            className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="mr-assign-confirm-btn"
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium inline-flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <User className="h-4 w-4" />}
            Assign Meeting
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function MeetingReview() {
  const { transcriptId } = useParams();
  const navigate = useNavigate();

  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [assigning, setAssigning] = useState(false);
  const [showCreateLead, setShowCreateLead] = useState(false);
  const [assignConfirm, setAssignConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [restoreBusy, setRestoreBusy] = useState(false);

  // Consultation Summary state
  const [consultSummary, setConsultSummary] = useState(null);
  const [csLoading, setCsLoading]           = useState(false);
  const [csGenerating, setCsGenerating]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/meeting-transcripts/${transcriptId}`);
      setMeeting(data);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else toast.error("Could not load meeting details");
    } finally { setLoading(false); }
  }, [transcriptId]);

  useEffect(() => { load(); }, [load]);

  // Load existing consultation summary for this transcript
  useEffect(() => {
    if (!transcriptId) return;
    setCsLoading(true);
    api.get(`/consultation-summaries/by-transcript/${transcriptId}`)
      .then(({ data }) => setConsultSummary(data.summary || null))
      .catch(() => setConsultSummary(null))
      .finally(() => setCsLoading(false));
  }, [transcriptId]);

  // Poll every 3 s while Allison is generating
  useEffect(() => {
    if (!consultSummary || consultSummary.status !== "generating") return;
    const id = setInterval(() => {
      api.get(`/consultation-summaries/by-transcript/${transcriptId}`)
        .then(({ data }) => { if (data.summary) setConsultSummary(data.summary); })
        .catch(() => {});
    }, 3000);
    return () => clearInterval(id);
  }, [consultSummary?.status, transcriptId]);

  const handleGenerateCS = async () => {
    if (!meeting) return;
    setCsGenerating(true);
    try {
      const { data } = await api.post("/consultation-summaries/generate", {
        transcript_id: transcriptId,
        analysis_id:   meeting.analysis_id || null,
      });
      setConsultSummary(data.summary);
      if (data.summary?.status === "generating") {
        toast.success("Allison is preparing the Consultation Summary — you can leave this page.");
      } else {
        toast.success("Consultation summary ready — review and approve before sending.");
      }
      navigate(`/consultation-summary/${data.summary.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate summary");
    } finally { setCsGenerating(false); }
  };

  // ── Derived context ──────────────────────────────────────────────────────
  const zoomCtx       = meeting?.meeting_context?.zoom || {};
  const transcriptCtx = meeting?.meeting_context?.transcript || {};
  const matchingCtx   = meeting?.meeting_context?.matching || {};
  const participants  = zoomCtx.participants || [];
  const suggestedMatches = matchingCtx.suggested_matches || [];

  const meetingDate = zoomCtx.started_at || zoomCtx.scheduled_at || meeting?.created_at;
  const topic       = zoomCtx.topic || meeting?.title || "Zoom Meeting";

  const intakeLabel = INTAKE_STATUS_LABELS[meeting?.intake_status]
                   || INTAKE_REASON_LABELS[meeting?.intake_reason]
                   || null;

  // ── Assignment helpers ────────────────────────────────────────────────────
  function buildEvidence(candidate) {
    return {
      score:               candidate?.score ?? null,
      reason:              candidate?.reason ?? null,
      detected_names:      transcriptCtx.detected_names || [],
      prospect_name:       transcriptCtx.prospect_name  || null,
      participant_email_match: participants.some(
        p => p.email && candidate?.subtitle &&
             p.email.toLowerCase() === candidate.subtitle.toLowerCase()
      ),
    };
  }

  function openConfirmFromCandidate(match) {
    setAssigning(false);
    setAssignConfirm({
      contact: { id: match.id, name: match.title, type: match.type, email: match.subtitle },
      evidence: buildEvidence(match),
    });
  }

  function openConfirmFromSearch(contact) {
    setAssigning(false);
    setAssignConfirm({ contact, evidence: buildEvidence(null) });
  }

  async function handleAssign() {
    if (!assignConfirm) return;
    setConfirmBusy(true);
    try {
      await api.post(`/meeting-transcripts/${transcriptId}/assign`, { client_id: assignConfirm.contact.id });
      toast.success(`Assigned to ${assignConfirm.contact.name} — analysis started`);
      setAssignConfirm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assignment failed");
    } finally { setConfirmBusy(false); }
  }

  async function handleArchive() {
    setArchiveBusy(true);
    try {
      await api.post(`/meeting-transcripts/${transcriptId}/archive`, { reason: "not_client_related" });
      toast.success("Meeting archived");
      setShowArchiveConfirm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Archive failed");
    } finally { setArchiveBusy(false); }
  }

  async function handleRestore() {
    setRestoreBusy(true);
    try {
      await api.post(`/meeting-transcripts/${transcriptId}/restore`);
      toast.success("Meeting restored");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    } finally { setRestoreBusy(false); }
  }

  // ── Loading / not-found ───────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="mr-loading">
      <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4" data-testid="mr-not-found">
      <div className="h-14 w-14 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center">
        <Video className="h-7 w-7 text-slate-400" />
      </div>
      <h1 className="text-xl font-bold text-slate-900">Meeting not found</h1>
      <p className="text-sm text-slate-500 text-center max-w-xs">
        This meeting record could not be located. It may have been removed or the link may be incorrect.
      </p>
      <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2F80ED] hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to Live Operations
      </button>
    </div>
  );

  if (!meeting) return null;

  const ana = meeting.analysis;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="meeting-review-page">

      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors"
          data-testid="mr-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Live Operations
        </button>
        {!meeting.is_archived && (
          <button
            onClick={() => setShowArchiveConfirm(true)}
            data-testid="mr-archive-btn"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-amber-700 hover:bg-amber-50 border border-slate-200 hover:border-amber-200 rounded-lg px-3 py-1.5 transition-colors"
          >
            <Archive className="h-3.5 w-3.5" /> Archive / Not Client Related
          </button>
        )}
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-5">

        {/* ── Archived banner ─────────────────────────────────────────── */}
        {meeting.is_archived && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="mr-archived-banner">
            <Archive className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-800">Archived — Not Client Related</p>
              {meeting.archived_by_name && (
                <p className="text-xs text-amber-700 mt-0.5">
                  By {meeting.archived_by_name}{meeting.archived_at && ` · ${fmtDate(meeting.archived_at)}`}
                </p>
              )}
            </div>
            <button
              onClick={handleRestore}
              disabled={restoreBusy}
              data-testid="mr-restore-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 hover:text-amber-900 border border-amber-300 hover:border-amber-500 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50 shrink-0"
            >
              {restoreBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
              Restore
            </button>
          </div>
        )}

        {/* ── Meeting header ──────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                <Video className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900 leading-snug" data-testid="mr-meeting-title">
                  {topic}
                </h1>
                <p className="text-sm text-slate-500 mt-0.5" data-testid="mr-meeting-date">
                  {fmtDate(meetingDate)}
                  {zoomCtx.duration_minutes && ` · ${fmtDuration(zoomCtx.duration_minutes)}`}
                </p>
              </div>
            </div>
            <StatusBadge meeting={meeting} />
          </div>

          {/* Meta row */}
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
            {meeting.provider && (
              <span>
                <span className="font-semibold uppercase text-slate-400 tracking-wide mr-1">Source</span>
                {meeting.provider}
              </span>
            )}
            {meeting.external_id && (
              <span data-testid="mr-zoom-id">
                <span className="font-semibold uppercase text-slate-400 tracking-wide mr-1">Zoom ID</span>
                {meeting.external_id}
              </span>
            )}
            {intakeLabel && (
              <span data-testid="mr-intake-label">
                <span className="font-semibold uppercase text-slate-400 tracking-wide mr-1">Status</span>
                {intakeLabel}
              </span>
            )}
          </div>
        </div>

        {/* ── Meeting Context ─────────────────────────────────────────── */}
        {(participants.length > 0 ||
          transcriptCtx.detected_names?.length > 0 ||
          transcriptCtx.detected_topics?.length > 0) && (
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="mr-context-section">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-4">Meeting Context</p>

            {/* Participants */}
            {participants.length > 0 && (
              <div className="mb-4">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1">
                  <Users className="h-3 w-3" /> Participants
                </p>
                <div className="space-y-2">
                  {participants.map((p, i) => (
                    <div key={i} className="flex items-center gap-3" data-testid={`mr-participant-${i}`}>
                      <div className="h-6 w-6 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                        <User className="h-3 w-3 text-slate-400" />
                      </div>
                      <span className="text-sm font-medium text-slate-700 min-w-0">{p.display_name || "—"}</span>
                      {p.email
                        ? <span className="text-sm text-slate-500">{p.email}</span>
                        : <span className="text-xs text-slate-300 italic">no email</span>
                      }
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Names detected in transcript */}
            {transcriptCtx.detected_names?.length > 0 && (
              <div className="mb-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1">
                  <FileText className="h-3 w-3" /> Names detected in transcript
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {transcriptCtx.detected_names.map((n, i) => (
                    <span
                      key={i}
                      className="text-xs bg-blue-50 text-blue-700 border border-blue-100 rounded-full px-2.5 py-0.5 font-medium"
                      data-testid={`mr-detected-name-${i}`}
                    >
                      {n}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Topics discussed */}
            {transcriptCtx.detected_topics?.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1">
                  <Tag className="h-3 w-3" /> Topics discussed
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {transcriptCtx.detected_topics.slice(0, 6).map((t, i) => (
                    <span key={i} className="text-xs bg-slate-100 text-slate-600 rounded-full px-2.5 py-0.5">{t}</span>
                  ))}
                  {transcriptCtx.detected_topics.length > 6 && (
                    <span className="text-xs text-slate-400">+{transcriptCtx.detected_topics.length - 6} more</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Meeting Preview ─────────────────────────────────────────── */}
        <MeetingPreviewSection transcriptId={transcriptId} transcriptCtx={transcriptCtx} />

        {/* ── Possible Matches ────────────────────────────────────────── */}
        {!meeting.is_archived && !meeting.client_id && (
          <PossibleMatchesSection
            matches={suggestedMatches}
            participants={participants}
            transcriptCtx={transcriptCtx}
            onAssign={openConfirmFromCandidate}
          />
        )}

        {/* ── Client Assignment ────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="mr-assignment-card">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Client Assignment</p>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-slate-400 shrink-0" />
              {meeting.client_id ? (
                <span className="text-sm font-semibold text-slate-800" data-testid="mr-client-name">
                  {meeting.client_name || meeting.client_id}
                </span>
              ) : (
                <span className="text-sm text-slate-500 italic" data-testid="mr-unassigned-label">
                  Unassigned — no client matched
                </span>
              )}
            </div>
            {meeting.client_id ? (
              /* Assigned — direct link to that client's workspace using canonical client_id */
              <button
                onClick={() => navigate(`/client-workspace/${meeting.client_id}`)}
                data-testid="mr-open-workspace-btn"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2F80ED] bg-blue-50 hover:bg-[#2F80ED] hover:text-white border border-[#2F80ED]/30 hover:border-[#2F80ED] rounded-lg px-3 py-1.5 transition-colors"
              >
                Open Client Workspace <ArrowRight className="h-3.5 w-3.5" />
              </button>
            ) : !meeting.is_archived && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setAssigning(v => !v)}
                  data-testid="mr-open-assign-btn"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2F80ED] hover:text-white hover:bg-[#2F80ED] border border-[#2F80ED]/40 hover:border-[#2F80ED] rounded-lg px-3 py-1.5 transition-colors"
                >
                  <Search className="h-3.5 w-3.5" /> Assign to Client
                </button>
                <button
                  onClick={() => setShowCreateLead(true)}
                  data-testid="mr-create-lead-btn"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-white hover:bg-emerald-600 border border-emerald-300 hover:border-emerald-600 rounded-lg px-3 py-1.5 transition-colors"
                >
                  <UserPlus className="h-3.5 w-3.5" /> Create New Lead
                </button>
              </div>
            )}
          </div>

          {assigning && !meeting.is_archived && (
            <AssignSearchPanel
              onSelectContact={openConfirmFromSearch}
              onCancel={() => setAssigning(false)}
            />
          )}
        </div>

        {/* ── Analysis action ─────────────────────────────────────────── */}
        {ana && meeting.client_id && !meeting.is_archived && (
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="mr-analysis-card">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-3">Case Analysis</p>
            {ana.status === "ready_for_review" && (
              <button
                onClick={() => navigate(`/case-analysis/${meeting.client_id}`)}
                data-testid="mr-review-analysis-btn"
                className="inline-flex items-center gap-1.5 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 transition-colors"
              >
                <Video className="h-4 w-4" /> Review Analysis <ExternalLink className="h-3.5 w-3.5" />
              </button>
            )}
            {(ana.status === "approved" || ana.approval_status === "approved") && (
              <button
                onClick={() => navigate(`/case-analysis/${meeting.client_id}`)}
                data-testid="mr-view-analysis-btn"
                className="inline-flex items-center gap-1.5 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-4 py-2 transition-colors"
              >
                <CheckCircle2 className="h-4 w-4" /> View Analysis <ExternalLink className="h-3.5 w-3.5" />
              </button>
            )}
            {ana.status === "processing" && (
              <div className="flex items-center gap-2 text-sm text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Analysis processing…
              </div>
            )}
          </div>
        )}

        {/* ── Consultation Summary (optional) ─────────────────────────── */}
        {/* Shown whenever the meeting is matched to a client/lead — no analysis required */}
        {!meeting.is_archived && meeting.client_id && (
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="mr-consultation-summary-card">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Consultation Summary
              </p>
              <span className="text-[10px] text-slate-400 italic">Optional — not required to close this meeting</span>
            </div>

            {csLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : consultSummary ? (
              <div className="space-y-3">
                {/* Status badge + navigate */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {{
                      generating:        <><Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-500" /><span className="text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5">Allison is preparing…</span></>,
                      generation_failed: <><AlertCircle className="h-3.5 w-3.5 text-red-500" /><span className="text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">Generation failed</span></>,
                      draft:             <><Edit3 className="h-3.5 w-3.5 text-amber-500" /><span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Draft v{consultSummary.version}</span></>,
                      approved:          <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /><span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Approved — ready to send</span></>,
                      sent:              <><Send className="h-3.5 w-3.5 text-blue-600" /><span className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">Sent to {consultSummary.delivery?.recipient_email}</span></>,
                    }[consultSummary.status] || null}
                  </div>
                  {consultSummary.status === "generation_failed" ? (
                    <button
                      onClick={handleGenerateCS}
                      disabled={csGenerating}
                      data-testid="mr-retry-consultation-summary-btn"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:underline disabled:opacity-50"
                    >
                      {csGenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                      Retry
                    </button>
                  ) : consultSummary.status !== "generating" ? (
                    <button
                      onClick={() => navigate(`/consultation-summary/${consultSummary.id}`)}
                      data-testid="mr-open-consultation-summary-btn"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2F80ED] hover:underline"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      {consultSummary.status === "draft" ? "Review & Approve" : "View Summary"}
                    </button>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-500">
                  Generate a polished client-facing summary of this consultation.
                  Staff must review and approve before it can be sent.
                  Works for both Leads and Clients — no engagement required.
                </p>
                <button
                  onClick={handleGenerateCS}
                  disabled={csGenerating}
                  data-testid="mr-generate-consultation-summary-btn"
                  className="inline-flex items-center gap-2 text-sm font-semibold bg-slate-800 hover:bg-slate-900 text-white rounded-xl px-4 py-2.5 transition-colors disabled:opacity-50"
                >
                  {csGenerating
                    ? <><Loader2 className="h-4 w-4 animate-spin" /> Requesting…</>
                    : <><FileText className="h-4 w-4" /> Generate Consultation Summary</>}
                </button>
              </div>
            )}
          </div>
        )}

      </div>

      {/* ── Archive confirm overlay ─────────────────────────────────────── */}
      {showArchiveConfirm && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => setShowArchiveConfirm(false)}
          data-testid="mr-archive-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm mx-4 p-6"
            onClick={e => e.stopPropagation()}
            data-testid="mr-archive-confirm-dialog"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="h-10 w-10 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center">
                <Archive className="h-5 w-5 text-amber-600" />
              </div>
              <button onClick={() => setShowArchiveConfirm(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <h3 className="text-base font-bold text-slate-900 mb-2">Archive this meeting?</h3>
            <p className="text-sm text-slate-500 mb-5 leading-relaxed">
              This meeting will be marked as not client related and removed from Live Operations.
              The record is preserved for audit purposes and can be restored.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowArchiveConfirm(false)}
                disabled={archiveBusy}
                className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                data-testid="mr-archive-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleArchive}
                disabled={archiveBusy}
                className="px-4 py-2 text-sm rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-medium inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="mr-archive-confirm"
              >
                {archiveBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
                Archive Meeting
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Assign confirmation dialog ──────────────────────────────────── */}
      <AssignConfirmDialog
        confirm={assignConfirm}
        onCancel={() => setAssignConfirm(null)}
        onConfirm={handleAssign}
        busy={confirmBusy}
      />

      {/* ── Create New Lead from Meeting ────────────────────────────────── */}
      {showCreateLead && (
        <CreateLeadModal
          participants={participants}
          prospectName={transcriptCtx.prospect_name}
          meetingTitle={meeting.title}
          transcriptId={transcriptId}
          onSuccess={(newLeadId) => {
            setShowCreateLead(false);
            navigate(`/client-workspace/${newLeadId}`);
          }}
          onCancel={() => setShowCreateLead(false)}
        />
      )}

    </div>
  );
}
